<template>
  <div class="article-watch-item" :class="cssClasses">
    <div class="article-watch-item__cell">
      <oie-base-image
        :images="imageUrl"
        class="article-watch-item__image"
        :alt-text="articleWatch.product.name"
        :image-width="mq('to-medium') ? '130px' : '82px'"
        :image-height="mq('to-medium') ? '130px' : '82px'"></oie-base-image>
    </div>

    <div class="article-watch-item__cell">
      <a :href="articleWatch.product.items[0].url" class="article-watch-item__name">
        {{ articleWatch.product.name }}
      </a>
      <p class="article-watch-item__id">
        {{ `${tStatic('articleNumberLabel')} ${articleWatch.product.items[0].id}` }}
      </p>
      <a class="article-watch-item__link" :href="articleWatch.product.items[0].url">
        {{ t('productLinkLabel') }}
        <oie-use-svg svg="arrow"></oie-use-svg>
      </a>
    </div>

    <div class="article-watch-item__cell">
      <p class="article-watch-item__date">
        <span>{{ `${t('addedLabel')}:` }}</span>
        {{ (new Date(articleWatch.createdAt)).toLocaleDateString(clientModel.currentLanguage, dateOptions) }}
      </p>
    </div>

    <div class="article-watch-item__cell article-watch-item__price">
      <p>{{ price !== 0 ? formatPrice(price) : '' }}</p>
      <span>{{ useVat ? tStatic('priceIncludingVat') : tStatic('priceExcludingVat') }}</span>
    </div>

    <div class="article-watch-item__cell">
      <oie-product-status
        :status="stock.status"
        :text="stock.message"
        css-classes="article-watch-item__status">
      </oie-product-status>
    </div>

    <div class="article-watch-item__cell">
      <oie-base-button
        :css-classes="['button--with-border', 'button--green', 'product-actions__buy-button', 'article-watch-item__buy']"
        :text="tStatic('addToCartButtonLabel')"
        :disabled="hasNoPrice"
        @click="addItemToCart()">
      </oie-base-button>
    </div>

    <div class="article-watch-item__cell">
      <oie-base-button
        css-classes="button--icon-only article-watch-item__delete"
        class="article-watch-list__button-icon"
        @click="showModal">
        <template slot="icon">
          <oie-use-svg svg="trashcan" width="24" height="24"></oie-use-svg>
        </template>
      </oie-base-button>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import gtm from '@/core/enhanced-ecommerce-gtm'
import { listTypes } from '@/core/analytics-variables'

export default {
  props: {
    articleWatch: {
      type: Object,
      required: true
    }
  },
  data: () => ({
    stock: {
      message: '',
      status: ''
    },
    dateOptions: {
      day: 'numeric',
      month: 'long'
    },
    price: 0
  }),
  computed: {
    ...mapGetters(['enableGtm', 'useVat']),
    hasNoPrice() {
      return this.articleWatch.product.items[0].customerPrice === null || this.articleWatch.product.items[0].customerPrice === undefined || this.articleWatch.product.items[0].customerPrice === 0
    },
    imageUrl() {
      return this.articleWatch.product.imageUrl ? this.articleWatch.product.imageUrl : ''
    }
  },
  watch: {
    articleWatch: function() {
      this.setPrice()
    }
  },
  mounted() {
    this.setStock()
    this.setPrice()
  },
  methods: {
    ...mapActions('commerce', ['addToCart']),
    addItemToCart() {
      const product = this.articleWatch.product
      this.addToCart({ itemId: product.items[0].id, productId: product.id, purchasedOnCategory: this.clientModel.analyticsCategory, purchasedToVehicle: this.clientModel.analyticsVehicle }).then((data) => {
        if (this.enableGtm && data.cart) {
          const addedProduct = data.cart.rows.find((x => x.itemId === product.items[0].id) && (x => x.productId === product.id))

          const gtmObject = gtm.createGtmItem(
            addedProduct.itemId,
            addedProduct.displayName,
            addedProduct.item.purchasedToVehicle,
            addedProduct.item.purchasedOnCategory,
            this.formatPriceWithoutCurrency(addedProduct.priceExVat),
            1,
            listTypes.ARTICLE_WATCH)

          gtm.addToCart(gtmObject)
        }
      })
    },
    setStock() {
      var number = this.articleWatch.product.items[0].stock
      var threshold = this.getProductFewInStock()

      if (number === undefined) { number = 0 }

      if (threshold === undefined) { threshold = 0 }

      if (number >= threshold) {
        this.stock.status = 'in-stock'
        this.stock.message = this.tStatic('stockAvailableLabel')
      } else if (number > 0) {
        this.stock.status = 'few-stock'
        this.stock.message = this.tStatic('stockFewLabel')
      } else if (this.articleWatch.product.items[0].isOrderItem) {
        this.stock.status = 'is-order-item'
        this.stock.message = this.tStatic('stockIsOrderItemLabel')
      } else {
        this.stock.status = 'no-stock'
        this.stock.message = this.tStatic('stockNoneLabel')
      }
    },
    setPrice() {
      const item = this.articleWatch.product.items[0]
      if (item.customerPrice == null || item.customerPrice <= 0) {
        this.price = 0
        return
      }

      if (item.customerPrice.campaignPrice != null) {
        this.price = item.customerPrice.campaignPrice
        return
      }

      if (item.customerPrice.customerPrice != null) {
        this.price = item.customerPrice.customerPrice
        return
      }

      this.price = item.customerPrice.listPrice
    },
    showModal() {
      this.publish('modal.show.remove-article-watch', { id: this.articleWatch.id, onHide: this.onModalHide })
    },
    onModalHide() {
      this.$emit('onModalHide')
    }
  }
}
</script>

<style lang="scss">
  .article-watch-item {
    position: relative;
    padding: 3rem 1rem 1rem 1rem;
    display: flex;
    flex-direction: column;
    border: 1px solid color(gray-4);
    box-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
    background-color: #fff;
  }

  .article-watch-item__image {
    margin: 0 auto;
  }

  .article-watch-item__name {
    @include body-bold;
    text-decoration: none;
    color: color(gray-1)
  }

  .article-watch-item__id {
    margin: 0;
    @include caption-semi-bold;
    color: color(gray-2)
  }

  .article-watch-item__link {
    display: none;
    @include reset-link;
  }

  .article-watch-item__date {
    margin: 1rem 0;

    > span {
      @include body-smaller-semi-bold;
    }
  }

  .article-watch-item__price {
    margin-bottom: 1rem;

    > p {
      @include h3-semi-bold;
      line-height: 1.5rem;
      height: 1.5rem;
      margin: 0;
    }

    > span {
      @include caption;
      line-height: 1.3125rem;
      margin-top: -3px;
    }
  }

  .article-watch-item__status {
    position: absolute;
    top: 1rem;
    left: 1rem;
  }

  .article-watch-item__buy {
    margin: 0 !important;
    width: 100% !important;

    &:disabled {
      background-color: color(gray-4);
      box-shadow: inset 0 -0.4375rem color(gray-3);
      opacity: 1 !important;

      > span {
        color: color(gray-2);
      }
    }
  }

  .article-watch-item__delete {
    position: absolute;
    top: 1rem;
    right: 1rem;
  }

  @include media(medium) {
    .article-watch-item {
      padding: 1.5rem;
      flex-direction: row;
      align-items: center;

      .article-watch-item__cell {
        display: flex;
        flex-direction: column;
        justify-content: center;
        padding: 0 0.5rem;
      }

      > :first-child {
        width: 15%;
      }

      > :nth-child(2) {
        width: 30%;
      }

      > :nth-child(3) {
        width: 10%;
      }

      > :nth-child(4) {
        width: 15%;
      }

      > :nth-child(5) {
        width: 10%;
      }

      > :nth-child(6) {
        width: 10%;
        display: flex;
        justify-content: center;
      }

      > :nth-child(7) {
        width: 8%;
        display: flex;
        justify-content: center;
      }
    }

    .article-watch-item__image {
      margin: 0;
    }

    .article-watch-item__link {
      display: block;
    }

    .article-watch-item__date {
      margin: 0;

      > span {
        display: none;
      }
    }

    .article-watch-item__price {
      margin: 0;

      > p {
        @include body;
      }

      > span {
        display: none;
      }
    }

    .article-watch-item__status {
      position: static;
    }

    .article-watch-item__delete {
      position: static;
    }
  }
</style>
